<template>
  <v-form @submit.prevent="handleSubmit">
    <v-card :class="getSaleClass(sale)">
      <v-card-title class="d-flex align-center">
        <span>Sale {{ sale.ID }}</span>
        <v-spacer></v-spacer>
        <v-btn
          v-if="sale.submitted"
          color="secondary"
          class="ml-2"
          :loading="loading"
          @click="$emit('unsubmit')"
        >
          Unsubmit
        </v-btn>
        <v-btn
          v-else
          color="success"
          class="ml-2"
          :loading="loading"
          @click="$emit('submit')"
        >
          Submit
        </v-btn>
        <v-btn
          color="primary"
          class="ml-2"
          :loading="loading"
          type="submit"
        >
          Save
        </v-btn>
        <v-btn
          class="ml-2"
          :to="`/returns/initiate/${sale.ID}`"
          v-if="sale.submitted"
          color="warning"
        >
          Return
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Sale Info -->
        <v-row>
          <v-col cols="12" md="4">
            <div class="text-caption text-medium-emphasis">Store</div>
            <div>{{ formData.Site }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-caption text-medium-emphasis">PO Number</div>
            <div>{{ formData.PONumber }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-caption text-medium-emphasis">Vendor</div>
            <div>{{ formData.Vendor }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-caption text-medium-emphasis">Created Date</div>
            <div>{{ formData.CreatedDate ? formatDate(formData.CreatedDate) : ''}}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-caption text-medium-emphasis">Tracking Number</div>
            <div>{{ formData.trackingnumber || '—' }}</div>
          </v-col>
          <v-col cols="12" md="2">
            <div class="text-caption text-medium-emphasis">Sale Price</div>
            <div>{{ formatCurrency(formData.ebayprice) }}</div>
          </v-col>
          <v-col cols="12" md="2">
            <div class="text-caption text-medium-emphasis">Profit</div>
            <div>{{ formatCurrency(formData.ebayprice - formData.ActualMyCost - formData.ActualShipping - formData.amazonfees - formData.ebayfees) }}</div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-caption text-medium-emphasis">Submitted</div>
            <div>
              <v-icon 
                :icon="formData.submitted ? 'mdi-check-circle' : 'mdi-alert'"
                :color="formData.submitted ? 'success' : 'error'"
              ></v-icon>
              {{ formData.submitted || 'Not Yet' }} {{ formData.submitted_by ? 'by ' + formData.submitted_by : '' }}
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-caption text-medium-emphasis">Listing</div>
            <a
              :href="marketplaceUrl"
              class="text-decoration-none"
              target="_blank"
            >
              {{ formData.listingid }}
            </a>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Editable Fields -->
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="formData.itemnumber"
              label="Item Number"
              :disabled="loading"
              required
            />
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field
              v-model.number="formData.quantity"
              label="Quantity"
              type="number"
              :disabled="loading"
            />
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field
              v-model.number="formData.ActualMyCost"
              label="Cost"
              type="number"
              prefix="$"
              :disabled="loading"
            />
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field
              v-model.number="formData.ActualShipping"
              label="Shipping"
              type="number"
              prefix="$"
              :disabled="loading"
            />
          </v-col>
          <v-col v-if="sale.marketplace === 'Amazon'" cols="6" md="2">
            <v-text-field
              v-model.number="formData.amazonfees"
              label="Amazon Fees"
              type="number"
              prefix="$"
            />
          </v-col>
          <v-col v-if="sale.marketplace === 'eBay'" cols="6" md="2">
            <v-text-field
              v-model.number="formData.ebayfees"
              label="eBay Fees"
              type="number"
              prefix="$"
            />
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="formData.shippingname"
              label="Shipping Name"
              :disabled="loading"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="formData.shippingstreet1"
              label="Street 1"
              :disabled="loading"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="formData.shippingstreet2"
              label="Street 2"
              :disabled="loading"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="formData.city"
              label="City"
              :disabled="loading"
            />
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field
              v-model="formData.state"
              label="State"
              :disabled="loading"
            />
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field
              v-model="formData.zip"
              label="ZIP"
              :disabled="loading"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="formData.phone"
              label="Phone"
              :disabled="loading"
            />
          </v-col>
        </v-row>
        <v-row v-if="sale.Vendor === 'eBay'">
          <v-col cols="6" md="2">
            <v-text-field
              v-model="formData.invoice_id"
              label="eBay Trans Id"
              :disabled="loading"
            />
          </v-col>
          <v-col cols="6" md="3">
            <v-text-field
              v-model="formData.ebayorderid"
              label="eBay Order Number"
              :disabled="loading"
            />
          </v-col>
          <v-col cols="6" md="3">
            <v-select
              v-model="formData.vendorAccount"
              :items="ebayAccounts"
              label="Account"
              item-title="email"
              item-value="email"
              :disabled="loading"
            />
          </v-col>
          <v-col cols="6" md="4">
            <v-text-field
              v-model="formData.vendorLink"
              label="eBay Link"
              :disabled="loading"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="9">
            <v-text-field
              v-model="formData.Note"
              label="Note"
              :disabled="loading"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-checkbox
              v-model="formData.IgnoreVendorTrackingNumber"
              label="Ignore Vendor TrackingNumber"
              :disabled="loading"
            />
          </v-col>
        </v-row>

      </v-card-text>
    </v-card>
  </v-form>
</template>

<script setup>
import { ref, watch, defineEmits, defineProps, onMounted, computed } from 'vue'
import { getSaleClass } from '@/utils/SaleClass'
import { salesService } from '@/services/salesService'
import { formatDate } from '@/utils/helpers'

const props = defineProps({
  sale: {
    type: Object,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['save', 'return', 'submit', 'unsubmit'])

const formData = ref({...props.sale, IgnoreVendorTrackingNumber: !!props.sale.IgnoreVendorTrackingNumber })
const ebayAccounts = ref([])

const marketplaceUrl = computed(() => {
  if (formData.value.marketplace === 'Amazon') {
    return `https://www.amazon.com/dp/${formData.value.listingid}`
  } else if (formData.value.marketplace === 'eBay') {
    return `https://ebay.com/itm/${formData.value.listingid}`
  }
  return ''
})
onMounted(async () => {
  if (formData.value.Vendor === 'eBay' && !formData.value.vendorLink   && formData.value.ebayorderid) {
    formData.value.vendorLink = `https://order.ebay.com/ord/show?orderId=${formData.value.ebayorderid}`
  }
  ebayAccounts.value = await salesService.getSalesEbayAccounts()
})

watch(() => props.sale, (newSale) => {
  formData.value = { ...newSale }
}, { deep: true })

const handleSubmit = () => {
  emit('save', formData.value)
}

const formatCurrency = (value) => {
  return `$${Number(value || 0).toFixed(2)}`
}


</script>

<style scoped>
.text-caption {
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.25rem;
  letter-spacing: 0.0333333333em;
  margin-bottom: 4px;
}
</style> 